import { useState, useRef, useEffect } from 'react';
import type { MathContent } from '@/types';
import styles from './QuestionEditor.module.scss';
import { motion, AnimatePresence } from 'motion/react';
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

interface MathEditorProps {
    content: any; // Using any to avoid complex type casting initially, but ideally MathContent
    onChange: (content: MathContent) => void;
}

interface MathAnswerInputProps {
    value: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    placeholder?: string;
    mathLiveReady: boolean;
}

function MathAnswerInput({ value, onChange, onRemove, placeholder, mathLiveReady }: MathAnswerInputProps) {
    const [inputMode, setInputMode] = useState<'latex' | 'visual'>('visual');
    const mathFieldRef = useRef<any>(null);

    // Initialize MathLive when switching to visual mode or when ready
    useEffect(() => {
        if (inputMode === 'visual' && mathLiveReady && mathFieldRef.current) {
            // Check if element is upgraded
            if (mathFieldRef.current.tagName.toLowerCase() === 'math-field') {
                mathFieldRef.current.value = value;

                // Add listener if not already added (simple check)
                const handleChange = (evt: any) => {
                    onChange(evt.target.value);
                };

                mathFieldRef.current.addEventListener('input', handleChange);

                return () => {
                    if (mathFieldRef.current) {
                        mathFieldRef.current.removeEventListener('input', handleChange);
                    }
                };
            }
        }
    }, [inputMode, mathLiveReady, mathFieldRef.current]);

    // Update value if changed externally (e.g. initial load)
    useEffect(() => {
        if (mathFieldRef.current && mathFieldRef.current.value !== value && document.activeElement !== mathFieldRef.current) {
            mathFieldRef.current.value = value;
        }
    }, [value]);

    return (
        <div className={styles.editor__option}>
            <div className={styles.editor__optionHeader}>
                <div className={styles.editor__inputToggle}>
                    <button
                        className={`${styles.editor__toggleButton} ${inputMode === 'visual' ? styles['editor__toggleButton--active'] : ''}`}
                        onClick={() => setInputMode('visual')}
                        type="button"
                        disabled={!mathLiveReady}
                    >
                        Visual {!mathLiveReady && '(Loading...)'}
                    </button>
                    <button
                        className={`${styles.editor__toggleButton} ${inputMode === 'latex' ? styles['editor__toggleButton--active'] : ''}`}
                        onClick={() => setInputMode('latex')}
                        type="button"
                    >
                        LaTeX
                    </button>
                </div>
                <div style={{ flex: 1 }} />
                <button
                    className={styles.editor__removeButton}
                    onClick={onRemove}
                    title="Remove answer"
                >
                    ✕
                </button>
            </div>

            <div className={styles.editor__editorRow}>
                {inputMode === 'visual' ? (
                    mathLiveReady ? (
                        <div className={styles.editor__mathFieldWrapper}>
                            <math-field
                                ref={mathFieldRef}
                                class={styles.editor__mathField}
                            >
                                {value}
                            </math-field>
                        </div>
                    ) : (
                        <div className={styles.editor__loading}>
                            Loading visual editor...
                        </div>
                    )
                ) : (
                    <input
                        type="text"
                        className={styles.editor__input}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder || "e.g. x^2 + 2x + 1"}
                    />
                )}

                {value && (
                    <div style={{ marginTop: '8px', padding: '8px', background: 'var(--background)', borderRadius: '4px' }}>
                        <span style={{ fontSize: '0.8em', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Preview:</span>
                        <BlockMath math={value} />
                    </div>
                )}
            </div>
        </div>
    );
}

export function MathEditor({ content, onChange }: MathEditorProps) {
    // Initialize state from content or defaults
    const [acceptedAnswers, setAcceptedAnswers] = useState<string[]>(
        content?.type === 'math' ? content.acceptedAnswers || [] : []
    );
    const [mathLiveReady, setMathLiveReady] = useState(false);

    // Initialize MathLive
    useEffect(() => {
        if (!mathLiveReady) {
            import('mathlive')
                .then(() => {
                    setMathLiveReady(true);
                })
                .catch((error) => {
                    console.error('Failed to load MathLive:', error);
                });
        }
    }, []);

    // Update parent when state changes
    useEffect(() => {
        onChange({
            type: 'math',
            acceptedAnswers,
            allowDecimal: content?.allowDecimal,
            precision: content?.precision,
            requireSimplified: content?.requireSimplified,
            requiredForm: content?.requiredForm,
        });
    }, [acceptedAnswers]);

    const handleAddAnswer = () => {
        setAcceptedAnswers([...acceptedAnswers, '']);
    };

    const handleUpdateAnswer = (index: number, value: string) => {
        const updated = [...acceptedAnswers];
        updated[index] = value;
        setAcceptedAnswers(updated);
    };

    const handleRemoveAnswer = (index: number) => {
        setAcceptedAnswers(acceptedAnswers.filter((_, i) => i !== index));
    };

    return (
        <div className={styles.editor__field}>
            <label className={styles.editor__label}>
                Accepted Answers
                <span className={styles.required}>*</span>
            </label>
            <p className={styles.helperText} style={{ marginBottom: '16px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                {'Enter the correct answers. You can use the Visual Editor or standard LaTeX (e.g., \\frac{1}{2}, \\sqrt{x}).'}
            </p>

            <div className={styles.editor__optionsList}>
                <AnimatePresence>
                    {acceptedAnswers.map((answer, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <MathAnswerInput
                                value={answer}
                                onChange={(val) => handleUpdateAnswer(index, val)}
                                onRemove={() => handleRemoveAnswer(index)}
                                mathLiveReady={mathLiveReady}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            <button className={styles.editor__addButton} onClick={handleAddAnswer}>
                + Add Accepted Answer
            </button>

            {acceptedAnswers.length === 0 && (
                <p className={styles.editor__warning}>At least one accepted answer is required</p>
            )}
        </div>
    );
}
